import React, { useState, useContext, useEffect } from 'react';
import { GlobalContext } from '../context/GlobalContext';
import { successToast, errorToast } from '../lib/helpers';
import '../css/dashboard.css';
import haxios from '../lib/haxios'
import useApi from '../hooks/useApi';
import { Skeleton, SkeletonGroup } from '../components/Skeleton';
import { AlertCircle } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import moment from 'moment';
import { DateInput } from '@mantine/dates';
import { useLocation } from 'react-router-dom';

import clsx from 'clsx';
import MessageDialog from '../components/MessageDialog';
import VisitNotesList from '../components/VisitNotesList';
import MessageList from '../components/MessageList';
import CustomerSpendList from '../components/CustomerSpendList';
import BranchPerformance from '../components/InsightsDashboard/BranchPerformance';
import QuoteList from '../components/QuoteList';
import BranchDropdown from '../components/BranchDropdown';
import QuickActionsPanel from '../components/InsightsDashboard/QuickActionsPanel';
import EnhancedDashboard from '../components/InsightsDashboard/EnhancedDashboard';

import { MdCarRepair } from 'react-icons/md';
import { WrenchScrewdriverIcon, BoltIcon } from '@heroicons/react/24/solid';
import { GiOilBarrel, GiOilDrum } from 'react-icons/gi';
import { ClockIcon } from '@heroicons/react/24/outline';

function Dashboard() {
  const { uinfo, cinfo } = useContext(GlobalContext);
  const location = useLocation();
  const isOldDashboard = location.pathname === '/dashboard-old';
  const [isToday, setIsToday] = useState(true);
  const [isGross, setIsGross] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState(cinfo.Admin ? 'All' : uinfo.Branch);
  const [showAllCust, setShowAllCust] = useState(false)
  const [activeColorFilters, setActiveColorFilters] = useState([])
  const [activeRankFilters, setActiveRankFilters] = useState([])
  const [showZeroYesterdayOnly, setShowZeroYesterdayOnly] = useState(false)
  const [showZeroSpendModal, setShowZeroSpendModal] = useState(false)
  const [modalSelectedBranch, setModalSelectedBranch] = useState(null)
  const [modalSelectedDate, setModalSelectedDate] = useState(new Date())
  const [modalOperatorFilter, setModalOperatorFilter] = useState('')
  const [selectedOperator, setSelectedOperator] = useState(uinfo);

  // Zero spend analysis API calls
  const { data: branchSalesData, ...branchSalesFetch } = useApi({
    url: showZeroSpendModal && modalSelectedBranch && modalSelectedDate
      ? `/visiondashboard/zero-spend/branch-sales/${encodeURIComponent(modalSelectedBranch)}/${moment(modalSelectedDate).format('YYYY-MM-DD')}`
      : null,
    initial: null
  });

  const { data: zeroSpendAnalysis, ...zeroSpendFetch } = useApi({
    url: showZeroSpendModal && modalSelectedBranch && modalSelectedDate
      ? `/visiondashboard/zero-spend/analysis/${encodeURIComponent(modalSelectedBranch)}/${moment(modalSelectedDate).format('YYYY-MM-DD')}`
      : null,
    initial: null
  });

  const { data: operatorMatrix, ...matrixFetch } = useApi({
    url: showZeroSpendModal && modalSelectedBranch && modalSelectedDate
      ? `/visiondashboard/zero-spend/operator-matrix/${encodeURIComponent(modalSelectedBranch)}/${moment(modalSelectedDate).format('YYYY-MM-DD')}`
      : null,
    initial: null
  });

  // Helper function to get color based on percentage
  const getPercentageColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600 bg-green-50';
    if (percentage >= 40) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  // Trigger API calls when modal opens with valid branch and date
  useEffect(() => {
    if (showZeroSpendModal && modalSelectedBranch && modalSelectedDate) {
      branchSalesFetch.refetch();
      zeroSpendFetch.refetch();
      matrixFetch.refetch();
    }
  }, [showZeroSpendModal, modalSelectedBranch, modalSelectedDate]);

  // Customer status state
  const [customerStatuses, setCustomerStatuses] = useState({});
  const [loadingStatuses, setLoadingStatuses] = useState({});
  // Add state for phone fields used
  const [customerPhoneFields, setCustomerPhoneFields] = useState({});

  // Add state for oil purchase data
  const [customerOilData, setCustomerOilData] = useState({});
  const [loadingOilData, setLoadingOilData] = useState({});

  // Add state for opening hours data
  const [customerOpeningHours, setCustomerOpeningHours] = useState({});
  const [loadingOpeningHours, setLoadingOpeningHours] = useState(false);

  const { data: branches, ...branchFetch } = useApi({ url: '/salesassist/cplusassist/branches', initial: [] })
  const { data: performanceData, ...perfFetch } = useApi({ url: '/visiondashboard/operatorPerformance', initial: [] })
  const { data: quotesData, ...quotesFetch } = useApi({ url: '/salesassist/cplusassist/quotesData', initial: [] })
  const { data: commentCounts } = useApi({
    url: '/visiondashboard/messages/counts',
    initial: {}
  });

  const { data: customerSalesData, ...custSalesFetch } = useApi({
    url: `/visiondashboard/rankCustomers?Rep=${selectedOperator.Inits}`,
    initial: []
  });

  const [customerContactStatus, setCustomerContactStatus] = useState({});

  // Add useEffect to fetch contact status when operator changes
  useEffect(() => {
    const fetchContactStatus = async () => {
      if (!selectedOperator || !selectedOperator.Inits) return;

      try {
        const response = await haxios.get(
          `/visiondashboard/customer-contact-status?operator_inits=${selectedOperator.Inits}`
        );

        if (response.success) {
          setCustomerContactStatus(response.data || {});
        }
      } catch (error) {
        console.error('Error fetching contact status:', error);
        errorToast('Failed to load contact status');
      }
    };

    fetchContactStatus();
  }, [selectedOperator]);

  // Add function to toggle contact status
  const toggleContactStatus = async (accountId) => {
    try {
      const newStatus = !customerContactStatus[accountId];

      // Optimistically update UI
      setCustomerContactStatus(prev => ({
        ...prev,
        [accountId]: newStatus
      }));

      // Update in database
      const response = await haxios.post('/visiondashboard/customer-contact-status', {
        account_id: accountId,
        contacted: newStatus,
        operator_inits: selectedOperator.Inits
      });

      if (response.success) {
        successToast(`Customer ${newStatus ? 'marked as contacted' : 'unmarked'}`);
      } else {
        // Revert if there was an error
        setCustomerContactStatus(prev => ({
          ...prev,
          [accountId]: !newStatus
        }));
        errorToast('Failed to update contact status');
      }
    } catch (error) {
      console.error('Error updating contact status:', error);
      errorToast('Failed to update contact status');

      // Revert the optimistic update
      setCustomerContactStatus(prev => ({
        ...prev,
        [accountId]: !customerContactStatus[accountId]
      }));
    }
  };

  // Add useEffect to fetch opening hours data
  useEffect(() => {
    const fetchOpeningHours = async () => {
      if (!customerSalesData || customerSalesData.length === 0) return;

      setLoadingOpeningHours(true);
      setCustomerOpeningHours({});

      try {
        const customerAccounts = customerSalesData.map(customer => customer._id).filter(Boolean);

        if (customerAccounts.length === 0) {
          setLoadingOpeningHours(false);
          return;
        }

        const response = await haxios.post('/visiondashboard/customers/opening-status', {
          customerAccounts: customerAccounts
        });

        if (response.data) {
          setCustomerOpeningHours(response.data || {});
        }
      } catch (error) {
        console.error('Error fetching opening hours:', error);
        errorToast('Failed to load opening hours');
      } finally {
        setLoadingOpeningHours(false);
      }
    };

    fetchOpeningHours();
  }, [customerSalesData, selectedOperator]);

  useEffect(() => {
    const fetchOilData = async () => {
      if (!customerSalesData || customerSalesData.length === 0) return;

      // Clear existing oil data when operator changes
      setCustomerOilData({});
      setLoadingOilData({});

      // Create an array of promises instead of using forEach with async
      const fetchPromises = customerSalesData.map(async (customer) => {
        const acctNo = customer._id;
        if (!acctNo) return;

        setLoadingOilData(prev => ({ ...prev, [acctNo]: true }));

        try {
          const response = await haxios.get(`/visiondashboard/salesassist/getLastOil/${acctNo}`);

          // Use the functional form to ensure latest state
          setCustomerOilData(prev => ({
            ...prev,
            [acctNo]: response.data || response // Handle both formats
          }));
        } catch (error) {
          console.error(`Failed to fetch oil data for ${acctNo}:`, error);
        } finally {
          setLoadingOilData(prev => ({ ...prev, [acctNo]: false }));
        }
      });

      // Wait for all fetches to complete
      await Promise.all(fetchPromises);
    };

    fetchOilData();
  }, [customerSalesData, selectedOperator]);



  useEffect(() => {
    custSalesFetch.refetch();
    perfFetch.refetch();
  }, [selectedOperator]);

  // Fetch customer statuses when customer data changes
  useEffect(() => {
    const fetchCustomerStatuses = async () => {
      if (!customerSalesData || customerSalesData.length === 0) return;

      // Clear existing statuses when operator changes
      setCustomerStatuses({});
      setLoadingStatuses({});
      setCustomerPhoneFields({});

      customerSalesData.forEach(async (customer) => {
        const acctNo = customer._id;

        if (!acctNo || customerStatuses[acctNo]) return;

        setLoadingStatuses(prev => ({ ...prev, [acctNo]: true }));

        try {
          const response = await haxios.get(`/visiondashboard/salesassist/customer-status/${acctNo}`);


          if (response.success) {
            // Store the status
            setCustomerStatuses(prev => ({
              ...prev,
              [acctNo]: response.status
            }));

            // Store which phone field was used
            setCustomerPhoneFields(prev => ({
              ...prev,
              [acctNo]: {
                field: response.callData.phoneField || 'Tel',
                value: response.callData.phoneNumber,
                fieldsWithMatches: response.callData.fieldsWithMatches || {}
              }
            }));
          }
        } catch (error) {
          console.error(`Failed to fetch status for ${acctNo}:`, error);
          // Set a default status on error
          setCustomerStatuses(prev => ({
            ...prev,
            [acctNo]: { emoji: '❓', text: 'Unknown', code: 'ERROR' }
          }));
        } finally {
          setLoadingStatuses(prev => ({ ...prev, [acctNo]: false }));
        }
      });
    };

    fetchCustomerStatuses();
  }, [customerSalesData, selectedOperator]);

  const { data: salesMarginByBranchAndAccountToday } = useApi({ url: '/salesassist/cplusassist/salesMarginByBranchAndAccountToday', initial: [] })
  const { data: salesOperators } = useApi({ url: '/salesassist/cplusassist/getOperators', initial: [] })

  // Separate API call for the matrix modal operators (full sales system)
  const { data: matrixOperatorsResponse } = useApi({ url: '/visiondashboard/operators/codes', initial: { data: [] } })
  const matrixOperators = matrixOperatorsResponse?.data || []

  // Debug data when it loads
  useEffect(() => {
    if (operatorMatrix?.data && matrixOperators?.length > 0) {
      console.log('🔍 DEBUG: Operator Matrix Data');
      console.log('Matrix operators:', operatorMatrix.data.operators.map(op => op.inits));
      console.log('Matrix operators data:', matrixOperators.map(op => ({ keyCode: op.KeyCode, name: op.a1 })));
    }
  }, [operatorMatrix, matrixOperators]);

  const [activeTab, setActiveTab] = useState('spends');
  const [rebateSort, setRebateSort] = useState('progress');
  const clearColorFilters = () => setActiveColorFilters([])
  const clearRankFilters = () => setActiveRankFilters([])

  const [selectedCustomerForComments, setSelectedCustomerForComments] = useState(null);

  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Add voucher data state
  const [voucherData, setVoucherData] = useState(null);
  const [loadingVoucherData, setLoadingVoucherData] = useState(false);

  // Add useEffect to fetch voucher data when operator changes
  useEffect(() => {
    const fetchVoucherData = async () => {
      if (!selectedOperator || !selectedOperator.Inits) return;

      setLoadingVoucherData(true);
      try {
        const response = await haxios.get(
          `/visiondashboard/voucher-progress?Rep=${selectedOperator.Inits}`
        );

        console.log('Voucher data response:', response);

        if (response.success) {
          setVoucherData(response.data || {});
        }
      } catch (error) {
        console.error('Error fetching voucher data:', error);
        errorToast('Failed to load voucher data');
      } finally {
        setLoadingVoucherData(false);
      }
    };

    fetchVoucherData();
  }, [selectedOperator]);

  const ServiceIcons = ({ mot, oil, ev }) => (
    <div className="flex space-x-1">
      <MdCarRepair className={`w-4 h-4 ${mot ? 'text-green-600' : 'text-gray-400'}`} title="MOT" />
      <WrenchScrewdriverIcon className={`w-4 h-4 ${oil ? 'text-green-600' : 'text-gray-400'}`} title="Oil Service" />
      <BoltIcon className={`w-4 h-4 ${ev ? 'text-green-600' : 'text-gray-400'}`} title="EV Service" />
    </div>
  );

  // Oil Drum icon component that changes color based on the percentage left
  const OilDrumIcon = ({ percentageLeft }) => {
    // Determine color based on percentage left
    let color = 'text-gray-400'; // Default color if no data
    let tooltipText = 'No oil purchase data';

    if (percentageLeft !== undefined) {
      if (percentageLeft >= 70) {
        color = 'text-green-500';
        tooltipText = `${percentageLeft}% of waiting period remaining`;
      } else if (percentageLeft >= 30) {
        color = 'text-amber-500';
        tooltipText = `${percentageLeft}% of waiting period remaining`;
      } else {
        color = 'text-red-500';
        tooltipText = `${percentageLeft}% of waiting period remaining - Ready to call!`;
      }
    }

    return (
      <div className="flex justify-center" title={tooltipText}>
        <GiOilDrum className={`w-6 h-6 ${color}`} />
      </div>
    );
  };

  // Add this new component right after the existing OilDrumIcon component
  // Update the DualOilIndicator component
  const DualOilIndicator = ({ oilData }) => {
    // Extract data for both oil types
    const oil200L = oilData?.['20058OL'];
    const oil20L = oilData?.['2058OL'];

    // Helper function to determine color, tooltip and display info
    const getOilProperties = (data) => {
      if (!data) {
        return {
          bgColor: 'bg-gray-100',
          borderColor: 'border-gray-300',
          textColor: 'text-gray-400',
          tooltip: 'No purchase data',
          daysSince: '-',
          percentageLeft: null
        };
      }

      const percentageLeft = data.cycleInfo?.percentageLeft;
      const daysSince = data.cycleInfo?.daysSinceLastSale || 0;
      let bgColor, borderColor, textColor, tooltip;

      if (percentageLeft >= 70) {
        bgColor = 'bg-green-100';
        borderColor = 'border-green-500';
        textColor = 'text-green-700';
        tooltip = `${percentageLeft}% of cycle remaining (${daysSince} days since last purchase)`;
      } else if (percentageLeft >= 30) {
        bgColor = 'bg-amber-100';
        borderColor = 'border-amber-500';
        textColor = 'text-amber-700';
        tooltip = `${percentageLeft}% of cycle remaining (${daysSince} days since last purchase)`;
      } else {
        bgColor = 'bg-red-100';
        borderColor = 'border-red-500';
        textColor = 'text-red-700';
        tooltip = `${daysSince} days since last purchase - Ready to call!`;
      }

      return { bgColor, borderColor, textColor, tooltip, daysSince, percentageLeft };
    };

    const oil200Props = getOilProperties(oil200L);
    const oil20Props = getOilProperties(oil20L);

    return (
      <div className="flex flex-col w-24 gap-2">
        {/* 200L Oil indicator */}
        <div
          className={`flex items-center justify-between w-full px-2 py-1 rounded ${oil200Props.bgColor} border ${oil200Props.borderColor}`}
          title={oil200Props.tooltip}
        >
          <span className="text-xs font-semibold">200L</span>
          {oil200Props.percentageLeft !== null ? (
            <div className={`text-xs font-bold ${oil200Props.textColor}`}>
              {oil200Props.daysSince}d
            </div>
          ) : (
            <div className="w-3 h-3 rounded-full bg-gray-200"></div>
          )}
        </div>

        {/* 20L Oil indicator */}
        <div
          className={`flex items-center justify-between w-full px-2 py-1 rounded ${oil20Props.bgColor} border ${oil20Props.borderColor}`}
          title={oil20Props.tooltip}
        >
          <span className="text-xs font-semibold">20L</span>
          {oil20Props.percentageLeft !== null ? (
            <div className={`text-xs font-bold ${oil20Props.textColor}`}>
              {oil20Props.daysSince}d
            </div>
          ) : (
            <div className="w-3 h-3 rounded-full bg-gray-200"></div>
          )}
        </div>
      </div>
    );
  };

  // Add Opening Hours Tooltip Component
  const OpeningHoursTooltip = ({ openingHours, currentDay, isOpen, status, tooltipStatus, garageName }) => {
    if (!openingHours && status !== 'unknown') return null;

    const dayNames = {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    };

    const formatTime = (time) => {
      if (!time || time.trim() === '') return 'Closed';
      return time;
    };

    return (
      <div className="bg-white border rounded-lg shadow-lg p-3 min-w-48 max-w-xs" style={{ zIndex: 9999 }}>
        <div className="font-semibold text-sm mb-2 text-gray-800">
          {garageName || 'Opening Hours'}
        </div>

        {status === 'unknown' ? (
          <div className="text-xs text-gray-600 text-center py-2">
            No opening hours available
          </div>
        ) : (
          <div className="space-y-1 text-xs">
            {Object.entries(dayNames).map(([day, displayName]) => {
              const dayHours = openingHours[day];
              const isCurrentDay = day === currentDay;

              return (
                <div
                  key={day}
                  className={`flex justify-between ${isCurrentDay ? 'font-bold text-blue-600' : 'text-gray-600'}`}
                >
                  <span>{displayName}:</span>
                  <span>
                    {dayHours?.open && dayHours?.close && dayHours.open.trim() !== '' && dayHours.close.trim() !== ''
                      ? `${formatTime(dayHours.open)} - ${formatTime(dayHours.close)}`
                      : 'Closed'
                    }
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-2 pt-2 border-t text-xs">
          <span className={`font-semibold ${status === 'unknown' ? 'text-gray-500' :
            isOpen ? 'text-green-600' : 'text-red-600'
            }`}>
            {tooltipStatus}
          </span>
        </div>
      </div>
    );
  };

  // Opening Hours Status Indicator Component
  const OpeningStatusIndicator = ({ customerId, garageName }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const openingData = customerOpeningHours[customerId];

    if (loadingOpeningHours) {
      return (
        <div className="flex justify-center">
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      );
    }

    if (!openingData || openingData.noProfile) {
      return (
        <div className="flex justify-center" title="No opening hours data">
          <ClockIcon className="w-5 h-5 text-gray-400" />
        </div>
      );
    }

    const { isOpen, status, openingHours, currentDay } = openingData;

    // Determine colors and styling based on status
    let circleColor, iconColor, tooltipStatus;
    if (status === 'unknown') {
      circleColor = 'bg-gray-400 border-gray-500';
      iconColor = 'text-white';
      tooltipStatus = 'Unknown Hours';
    } else if (isOpen) {
      circleColor = 'bg-green-500 border-green-600';
      iconColor = 'text-white';
      tooltipStatus = 'Currently Open';
    } else {
      circleColor = 'bg-red-500 border-red-600';
      iconColor = 'text-white';
      tooltipStatus = 'Currently Closed';
    }

    return (
      <div
        className="relative flex justify-center"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center cursor-help ${circleColor}`}>
          <ClockIcon className={`w-3 h-3 ${iconColor}`} />
        </div>

        {showTooltip && (
          <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 z-50">
            <OpeningHoursTooltip
              openingHours={openingHours}
              currentDay={currentDay}
              isOpen={isOpen}
              status={status}
              tooltipStatus={tooltipStatus}
              garageName={garageName}
            />
          </div>
        )}
      </div>
    );
  };

  const handleCommentClick = (customer) => {
    setSelectedCustomer(customer);
    setMessageDialogOpen(true);
  };


  const formatPercent = (number) => {
    if (isNaN(number) || number === null || number === undefined) return '0.00%';
    return new Intl.NumberFormat('en-GB', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(number);
  };

  useEffect(() => {
    setActiveColorFilters([])
    setActiveRankFilters([])
    setShowZeroYesterdayOnly(false)
  }, [activeTab])


  const isLoading = (
    branchFetch.isLoading ||
    perfFetch.isLoading ||
    custSalesFetch.isLoading ||
    quotesFetch.isLoading
  )

  useEffect(() => {
    if (isLoading) return
    branchFetch.refetch()
    perfFetch.refetch()
    custSalesFetch.refetch()
    quotesFetch.refetch()
  }, [selectedBranch])

  if (!uinfo) {
    return <div>Loading performance data...</div>;
  }

  const toggleSwitch = () => {
    setIsToday(!isToday);
  };

  const handleBranchChange = (event) => {
    setSelectedBranch(event.target.value);
  };

  const customerSpends = customerSalesData
    .map((customer) => {
      const customerData = salesMarginByBranchAndAccountToday.find(el => el.account === customer._id);
      const messageData = commentCounts.data?.find(m => m.Acct === customer._id);

      return {
        ...customer,
        color: customer.totalSalesToday > 0 ? 'green' :
          (customerData?.quotesCount > 0 ? 'orange' : 'red'),
        messageCount: messageData?.messageCount || 0
      }
    })
    .sort((a, b) => b.color.localeCompare(a.color));

  const customerSpendsFiltered = customerSpends.filter((el) => {
    // Color filter
    const colorMatch = activeColorFilters.length === 0 || activeColorFilters.includes(el.color);
    // Rank filter
    const rankMatch = activeRankFilters.length === 0 || activeRankFilters.includes(el.CustomerRank);
    // Zero yesterday filter
    const zeroYesterdayMatch = !showZeroYesterdayOnly || (el.totalSalesYesterday === 0 || el.totalSalesYesterday === null || el.totalSalesYesterday === undefined || parseFloat(el.totalSalesYesterday) === 0);
    return colorMatch && rankMatch && zeroYesterdayMatch;
  });


  const filteredOutstandingQuotes = quotesData.OutstandingQuotesDetails?.filter(quote =>
    selectedBranch === 'All' || quote.Branch === selectedBranch
  ) || [];

  const getWorkingDaysLeft = () => {
    const today = moment();
    const endOfMonth = moment().endOf('month');
    let workingDays = 0;
    let current = today;

    while (current <= endOfMonth) {
      // 0 = Sunday, 6 = Saturday
      const dayOfWeek = current.day();
      if (dayOfWeek !== 0) { // Not Sunday
        workingDays += dayOfWeek === 6 ? 0.5 : 1; // Half day for Saturday
      }
      current = current.add(1, 'day');
    }

    return workingDays;
  };

  // Helper to generate the tooltip text for customer contact status
  const getStatusTooltip = (customerId) => {
    const status = customerStatuses[customerId];
    const phoneInfo = customerPhoneFields[customerId];

    if (!status) return 'Unknown status';

    let tooltip = status.text;

    // Add phone field info to tooltip if available
    if (phoneInfo) {
      const field = phoneInfo.field;
      const value = phoneInfo.value;

      if (field && value) {
        tooltip += ` (using ${field}: ${value})`;
      }
    }

    return tooltip;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 py-6 pt-4">
        {/* Enhanced Dashboard - only show if NOT on old dashboard route */}
        {!isOldDashboard && <EnhancedDashboard />}
        
        {/* Quick Actions Panel - only show if NOT on old dashboard route */}
        {!isOldDashboard && (
          <div className="mb-8 px-4 pt-4">
            <QuickActionsPanel />
          </div>
        )}

        {/* OLD DASHBOARD TABLES - show when on /dashboard-old route */}
        {isOldDashboard && (
        <>
        <div className="flex justify-between items-center mb-6">
        {/* {
          cinfo.Admin ? (
            <select value={selectedBranch} onChange={handleBranchChange} className="p-2 rounded border">
              <option value="All">All Branches</option>
              {branches.map((branch, index) => (
                <option key={index} value={branch.Branch}>{branch.Branch}</option>
              ))}
            </select>
          ) : (
            <div className="text-lg font-bold">{uinfo.Branch}</div>
          )
        } */}

        {/* <div className="flex items-center">
          <span className="mr-4 text-lg">{isToday ? 'Today' : 'Month'}</span>
          <label className="mx-2 switch">
            <input type="checkbox" checked={isToday} onChange={toggleSwitch} />
            <span className="slider round"></span>
          </label>
          <span className="ml-4 text-lg">{isGross ? 'Gross' : 'Nett'}</span>
          <label className="mx-2 switch">
            <input type="checkbox" checked={isGross} onChange={() => setIsGross(!isGross)} />
            <span className="slider round"></span>
          </label>
        </div> */}
      </div>


      <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
        <CurrentUserHeader
          uinfo={uinfo}
          currentUserData={performanceData.filter(data => data.Username === selectedOperator.Username)}
          isLoading={perfFetch.isLoading}
          isToday={isToday}
          isGross={isGross}
          selectedOperator={selectedOperator}
          setSelectedOperator={setSelectedOperator}
          salesOperators={salesOperators}
        />

        {uinfo.Status !== 'sales' && <BranchPerformance />}
      </div>
      {/* <div>
        {JSON.stringify(uinfo)}
      </div> */}
      {/* Customers Not Spent Today */}
      {/* Customer Spends Section */}
      <div className="flex flex-col gap-6 my-6 w-full">
        <div className="w-full bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setActiveTab('spends');
                  clearColorFilters();
                }}
              >
                Customer Spends
              </button>

              <button
                className={clsx(
                  'px-4 py-2 rounded-lg font-medium',
                  activeTab === 'rebates' ? 'bg-blue-500 text-white' : 'bg-gray-100'
                )}
                onClick={() => setActiveTab('rebates')}
              >
                Rebate Tracker
              </button>
              <button
                className={clsx(
                  'px-4 py-2 rounded-lg font-medium',
                  activeTab === 'vouchers' ? 'bg-blue-500 text-white' : 'bg-gray-100'
                )}
                onClick={() => setActiveTab('vouchers')}
              >
                Voucher Tracker
              </button>
              <button
                className={clsx(
                  'px-4 py-2 rounded-lg font-medium',
                  activeTab === 'analytics' ? 'bg-blue-500 text-white' : 'bg-gray-100'
                )}
                onClick={() => setActiveTab('analytics')}
              >
                Performance
              </button>
            </div>


            {activeTab === 'spends' && (
              <div className="flex items-center space-x-6">
                {/* Rank Filters */}
                <div className="flex items-center">
                  <div className="mr-2 text-sm font-medium">Filter by rank</div>
                  {(() => {
                    // Get unique ranks from the data
                    const availableRanks = [...new Set(customerSpends.map(customer => customer.CustomerRank).filter(Boolean))].sort();

                    return availableRanks.map((rank) => (
                      <div
                        key={rank}
                        className={clsx(
                          'mr-1 w-6 h-6 rounded-full cursor-pointer transition-all duration-200 flex items-center justify-center text-xs font-bold text-white',
                          rank === 'A' && 'bg-green-500',
                          rank === 'B' && 'bg-blue-500',
                          rank === 'C' && 'bg-orange-500',
                          rank === 'D' && 'bg-yellow-500',
                          rank === 'E' && 'bg-purple-500',
                          rank === 'F' && 'bg-red-500',
                          rank === 'N/A' && 'bg-gray-500',
                          activeRankFilters.includes(rank)
                            ? 'ring-2 ring-blue-500 ring-offset-2 scale-110'
                            : 'opacity-50 hover:opacity-100'
                        )}
                        onClick={() => {
                          if (activeRankFilters.includes(rank)) {
                            setActiveRankFilters(activeRankFilters.filter(r => r !== rank))
                          } else {
                            setActiveRankFilters([...activeRankFilters, rank])
                          }
                        }}
                      >
                        {rank}
                      </div>
                    ));
                  })()}
                </div>

                {/* Color Filters */}
                <div className="flex items-center">
                  <div className="mr-2 text-sm font-medium">Filter by color</div>
                  {/* Red button */}
                  <div
                    className={clsx(
                      'mr-2 w-7 h-7 bg-red-500 rounded-full cursor-pointer transition-all duration-200',
                      activeColorFilters.includes('red')
                        ? 'ring-2 ring-blue-500 ring-offset-2 scale-110'
                        : 'opacity-50 hover:opacity-100'
                    )}
                    onClick={() => {
                      if (activeColorFilters.includes('red')) {
                        setActiveColorFilters(activeColorFilters.filter(c => c !== 'red'))
                      } else {
                        setActiveColorFilters([...activeColorFilters, 'red'])
                      }
                    }}
                  />
                  {/* Orange button */}
                  <div
                    className={clsx(
                      'mr-2 w-7 h-7 bg-orange-500 rounded-full cursor-pointer transition-all duration-200',
                      activeColorFilters.includes('orange')
                        ? 'ring-2 ring-blue-500 ring-offset-2 scale-110'
                        : 'opacity-50 hover:opacity-100'
                    )}
                    onClick={() => {
                      if (activeColorFilters.includes('orange')) {
                        setActiveColorFilters(activeColorFilters.filter(c => c !== 'orange'))
                      } else {
                        setActiveColorFilters([...activeColorFilters, 'orange'])
                      }
                    }}
                  />
                  {/* Green button */}
                  <div
                    className={clsx(
                      'w-7 h-7 bg-green-500 rounded-full cursor-pointer transition-all duration-200',
                      activeColorFilters.includes('green')
                        ? 'ring-2 ring-blue-500 ring-offset-2 scale-110'
                        : 'opacity-50 hover:opacity-100'
                    )}
                    onClick={() => {
                      if (activeColorFilters.includes('green')) {
                        setActiveColorFilters(activeColorFilters.filter(c => c !== 'green'))
                      } else {
                        setActiveColorFilters([...activeColorFilters, 'green'])
                      }
                    }}
                  />
                </div>

                {/* Zero Yesterday Toggle */}
                <div className="flex items-center">
                  <div className="mr-2 text-sm font-medium">Zero yesterday</div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={showZeroYesterdayOnly}
                      onChange={() => setShowZeroYesterdayOnly(!showZeroYesterdayOnly)}
                    />
                    <div className={clsx(
                      "w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer",
                      showZeroYesterdayOnly ? "peer-checked:after:translate-x-full peer-checked:bg-blue-600" : ""
                    )}>
                      <div className={clsx(
                        "after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all",
                        showZeroYesterdayOnly ? "after:translate-x-5" : ""
                      )}></div>
                    </div>
                  </label>
                  {uinfo.Status === 'admin' && (
                    <button
                      onClick={() => setShowZeroSpendModal(true)}
                      className="ml-3 p-1 text-gray-500 hover:text-blue-600 transition-colors duration-200"
                      title="Zero Spend Analysis"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )}


            {/* <div className="flex items-center">
              <span className="ml-4 text-lg">{showAllCust ? 'Showing all' : 'Showing yours'}</span>
              <label className="mx-2 switch">
                <input type="checkbox" checked={showAllCust} onChange={() => setShowAllCust(!showAllCust)} />
                <span className="slider round"></span>
              </label>
            </div> */}
          </div>

          {/* Tab Content */}
          <div className="mt-4">
            {activeTab === 'spends' && (
              <div className="overflow-y-auto max-h-96" style={{ overflowX: 'visible' }}>
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center p-8 space-y-4">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-500">Loading customer data...</p>
                  </div>
                ) : (
                  <div className="relative">
                    <table className="min-w-full">
                      <thead className="sticky top-0 bg-white z-10">
                        <tr className="text-sm font-bold border-b">
                          <th className="px-2 py-1 bg-white"></th>
                          <th className="px-2 py-1 text-left bg-white">Acct</th>
                          <th className="px-2 py-1 text-left bg-white">Garage</th>
                          <th className="px-2 py-1 text-left bg-white">Rank</th>
                          <th className="px-2 py-1 text-left bg-white">30d</th>
                          <th className="px-2 py-1 text-left bg-white">Today</th>
                          <th className="px-2 py-1 text-left bg-white">Yesterday</th>
                          <th className="px-2 py-1 text-left bg-white">Services</th>
                          <th className="px-2 py-1 text-center bg-white">Call Status</th>
                          <th className="px-2 py-1 text-left bg-white">Comments</th>
                          <th className="px-2 py-1 text-center bg-white">Oil</th>
                          <th className="px-2 py-1 text-center bg-white">Open Now</th>
                          <th className="px-2 py-1 text-center bg-white">Contacted</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {customerSpendsFiltered
                          .map((customer, index) => (
                            <tr key={index} className="border-b">
                              <td className="px-2 py-1">
                                <div className={clsx(
                                  'w-5 h-5 rounded-full',
                                  customer.color === 'red' && 'bg-red-500',
                                  customer.color === 'orange' && 'bg-orange-500',
                                  customer.color === 'green' && 'bg-green-500'
                                )} />
                              </td>
                              <td className="px-2 py-1">{customer._id}</td>
                              <td className="px-2 py-1">{customer.GarageName}</td>
                              <td className="px-2 py-1">
                                <span className={clsx(
                                  'px-2 py-1 rounded-full text-white',
                                  customer.CustomerRank === 'A' && 'bg-green-500',
                                  customer.CustomerRank === 'B' && 'bg-blue-500',
                                  customer.CustomerRank === 'C' && 'bg-orange-500',
                                  customer.CustomerRank === 'D' && 'bg-yellow-500',
                                  customer.CustomerRank === 'E' && 'bg-purple-500',
                                  customer.CustomerRank === 'F' && 'bg-red-500',
                                  customer.CustomerRank === 'N/A' && 'bg-gray-500'
                                )}>
                                  {customer.CustomerRank || 'N/A'}
                                </span>
                              </td>
                              <td className="px-2 py-1">£{formatNumber(customer.totalSalesLast30Days)}</td>
                              <td className="px-2 py-1">£{formatNumber(customer.totalSalesToday)}</td>
                              <td className="px-2 py-1">£{formatNumber(customer.totalSalesYesterday)}</td>
                              <td className="px-2 py-1">
                                <ServiceIcons
                                  mot={customer.serviceFlags?.provides_mot}
                                  oil={customer.serviceFlags?.provides_oil_service}
                                  ev={customer.serviceFlags?.provides_ev_service}
                                />
                              </td>
                              <td className="px-2 py-1 text-center">
                                {loadingStatuses[customer._id] ? (
                                  <div className="w-4 h-4 mx-auto border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <div
                                    title={getStatusTooltip(customer._id)}
                                    className="text-lg cursor-help"
                                  >
                                    {customerStatuses[customer._id]?.emoji || '❓'}
                                  </div>
                                )}
                              </td>
                              <td
                                className="px-2 py-1 cursor-pointer hover:text-blue-500"
                                onClick={() => handleCommentClick({
                                  id: customer._id,
                                  garageName: customer.GarageName
                                })}
                              >
                                {customer.messageCount || 0}
                              </td>
                              <td className="px-2 py-1 text-center">
                                {loadingOilData[customer._id] ? (
                                  <div className="w-4 h-4 mx-auto border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <DualOilIndicator
                                    oilData={customerOilData[customer._id]}
                                  />
                                )}
                              </td>

                              <td className="px-2 py-1 text-center">
                                <OpeningStatusIndicator
                                  customerId={customer._id}
                                  garageName={customer.GarageName}
                                />
                              </td>

                              <td className="px-2 py-1 text-center">
                                <div className="flex justify-center">
                                  <button
                                    className={`w-6 h-6 rounded-full cursor-pointer flex items-center justify-center transition-colors duration-200 ${customerContactStatus[customer._id]
                                      ? 'bg-green-500 text-white'
                                      : 'bg-gray-200'
                                      }`}
                                    onClick={() => toggleContactStatus(customer._id)}
                                    title={customerContactStatus[customer._id] ? "Contacted" : "Not contacted"}
                                  >
                                    {customerContactStatus[customer._id] && (
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        {customerSpendsFiltered.length === 0 && (
                          <tr>
                            <td colSpan="13" className="py-2 text-center text-gray-500">
                              All customers have spent today.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}


            {activeTab === 'rebates' && (
              <div className="space-y-4">
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center p-8 space-y-4">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-500">Loading rebate data...</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center">
                      <select
                        className="p-2 rounded border"
                        onChange={(e) => setRebateSort(e.target.value)}
                        value={rebateSort}
                      >
                        <option value="progress">Sort by Progress to Next Tier</option>
                        <option value="current">Sort by Current Rebate Amount</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto p-2">
                      {customerSpendsFiltered
                        .filter(customer => customer.rebateAnalysis?.currentTier || customer.rebateAnalysis?.nextTier)
                        .sort((a, b) => {
                          switch (rebateSort) {
                            case 'progress':
                              const aProgress = (a.rebateAnalysis?.netMonthSales || 0) / (a.rebateAnalysis?.nextTier?.target || Infinity);
                              const bProgress = (b.rebateAnalysis?.netMonthSales || 0) / (b.rebateAnalysis?.nextTier?.target || Infinity);
                              return bProgress - aProgress;
                            case 'current':
                              return (b.rebateAnalysis?.currentRebateAmount || 0) - (a.rebateAnalysis?.currentRebateAmount || 0);
                            default:
                              return 0;
                          }
                        })
                        .map((customer, index) => {
                          const progressPercent = customer.rebateAnalysis?.nextTier ?
                            Math.min(100, Math.max(0,
                              (customer.rebateAnalysis.netMonthSales || 0) /
                              (customer.rebateAnalysis.nextTier.target || 1) * 100
                            )) : 0;

                          const progressColor =
                            progressPercent >= 90 ? 'bg-green-500' :
                              progressPercent >= 75 ? 'bg-green-400' :
                                progressPercent >= 50 ? 'bg-yellow-400' :
                                  progressPercent >= 25 ? 'bg-orange-400' :
                                    'bg-red-400';

                          return (
                            <div key={index} className="border rounded-lg p-4">
                              <div className="flex justify-between items-center mb-4">
                                <h3 className="font-bold">{customer.GarageName}</h3>
                                <span className={clsx(
                                  'text-sm px-2 py-1 rounded',
                                  customer.rebateAnalysis?.isGlobalRebate ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'
                                )}>
                                  {customer.rebateAnalysis?.isGlobalRebate ? 'Global' : 'Tiered'}
                                </span>
                              </div>

                              <div className="space-y-4">
                                <div>
                                  <div className="mb-2 flex justify-between text-sm">
                                    {customer.rebateAnalysis?.nextTier ? (
                                      <>
                                        <span>Progress to Next Tier</span>
                                        <span>
                                          £{formatNumber(customer.rebateAnalysis?.netMonthSales || 0)} /
                                          £{formatNumber(customer.rebateAnalysis?.nextTier?.target || 0)}
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <span>Monthly Sales</span>
                                        <span>£{formatNumber(customer.rebateAnalysis?.netMonthSales || 0)}</span>
                                      </>
                                    )}
                                  </div>
                                  {customer.rebateAnalysis?.nextTier ? (
                                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                      <div
                                        className={clsx(
                                          'h-full transition-all duration-300',
                                          progressColor
                                        )}
                                        style={{
                                          width: `${progressPercent}%`
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <div className="w-full h-2 bg-green-500 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-green-600 animate-pulse"
                                        style={{ width: '100%' }}
                                      />
                                    </div>
                                  )}
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <div className="text-gray-500">Current Rebate</div>
                                    <div className="font-bold">
                                      £{formatNumber(customer.rebateAnalysis?.currentRebateAmount || 0)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500">Required Daily</div>
                                    <div className="font-bold">
                                      £{formatNumber(customer.rebateAnalysis?.dailyMetrics?.dailySalesNeededForNextTier || 0)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500">Working Days Left</div>
                                    <div className="font-bold">
                                      {getWorkingDaysLeft()}
                                    </div>
                                  </div>
                                  <div>
                                    <div className={clsx(
                                      'text-sm',
                                      customer.rebateAnalysis?.nextTier ? 'text-gray-400 italic' : 'text-green-600'
                                    )}>
                                      {customer.rebateAnalysis?.nextTier ? (
                                        `Next tier: ${customer.rebateAnalysis.nextTier.percent}%`
                                      ) : (
                                        <>
                                          <div className="text-green-600">Maximum Tier Achieved!</div>
                                          <div className="text-gray-600">
                                            {customer.rebateAnalysis?.currentTier?.percent}% on all sales
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'vouchers' && (
              <div className="space-y-4">
                {loadingVoucherData ? (
                  <div className="flex flex-col items-center justify-center p-8 space-y-4">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-500">Loading voucher data...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Search Filter */}
                    <div className="flex items-center space-x-4">
                      <div className="relative flex-1 max-w-md">
                        <input
                          type="text"
                          placeholder="Search customers..."
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          onChange={(e) => {
                            const searchTerm = e.target.value.toLowerCase();
                            const filteredData = voucherData?.filter(customer =>
                              customer.Name.toLowerCase().includes(searchTerm) ||
                              customer.Acct.toLowerCase().includes(searchTerm) ||
                              customer.Area.toLowerCase().includes(searchTerm)
                            );
                            setVoucherData(prev => ({
                              ...prev,
                              filtered: filteredData || voucherData
                            }));
                          }}
                        />
                        <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <div className="text-sm text-gray-600">
                        {voucherData?.filtered?.length || voucherData?.length || 0} customers
                      </div>
                    </div>

                    {/* Voucher Progress Cards */}
                    <div className="grid gap-4 max-h-[600px] overflow-y-auto">
                      {(voucherData?.filtered || voucherData || [])
                        .sort((a, b) => {
                          const aProgress = (a.Sales / a.Target) * 100;
                          const bProgress = (b.Sales / b.Target) * 100;
                          return bProgress - aProgress; // Sort by closest to target first
                        })
                        .map((customer, index) => {
                          const progressPercent = Math.min(100, (customer.Sales / customer.Target) * 100);
                          const isAchieved = customer.Sales >= customer.Target;
                          const remaining = Math.max(0, customer.Target - customer.Sales);

                          // Determine colors based on progress
                          let progressColor, bgColor, borderColor, textColor;
                          if (isAchieved) {
                            progressColor = 'bg-green-500';
                            bgColor = 'bg-green-50';
                            borderColor = 'border-green-200';
                            textColor = 'text-green-800';
                          } else if (progressPercent >= 80) {
                            progressColor = 'bg-green-400';
                            bgColor = 'bg-green-50';
                            borderColor = 'border-green-200';
                            textColor = 'text-green-700';
                          } else if (progressPercent >= 60) {
                            progressColor = 'bg-yellow-400';
                            bgColor = 'bg-yellow-50';
                            borderColor = 'border-yellow-200';
                            textColor = 'text-yellow-700';
                          } else if (progressPercent >= 40) {
                            progressColor = 'bg-orange-400';
                            bgColor = 'bg-orange-50';
                            borderColor = 'border-orange-200';
                            textColor = 'text-orange-700';
                          } else {
                            progressColor = 'bg-red-400';
                            bgColor = 'bg-red-50';
                            borderColor = 'border-red-200';
                            textColor = 'text-red-700';
                          }

                          return (
                            <div
                              key={customer.Acct}
                              className={`p-6 rounded-lg border-2 ${bgColor} ${borderColor} transition-all duration-300 hover:shadow-lg ${isAchieved ? 'ring-2 ring-green-300 ring-opacity-50' : ''
                                }`}
                            >
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                  <div className="flex-shrink-0">
                                    {isAchieved ? (
                                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                      </div>
                                    ) : (
                                      <div className={`w-10 h-10 ${progressColor} rounded-full flex items-center justify-center`}>
                                        <span className="text-white font-bold text-sm">
                                          {customer.Rank}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <h3 className="font-bold text-lg text-gray-900">{customer.Name}</h3>
                                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                                      <span>Account: {customer.Acct}</span>
                                      <span>Area: {customer.Area}</span>
                                      <span>Rep: {customer.Rep}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-2xl font-bold text-gray-900">
                                    £{formatNumber(customer.Sales)}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    Target: £{formatNumber(customer.Target)}
                                  </div>
                                </div>
                              </div>

                              {/* Progress Bar */}
                              <div className="mb-3">
                                <div className="flex justify-between text-sm mb-1">
                                  <span className={textColor}>Progress</span>
                                  <span className={textColor}>{progressPercent.toFixed(1)}%</span>
                                </div>
                                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full ${progressColor} transition-all duration-1000 ease-out`}
                                    style={{ width: `${progressPercent}%` }}
                                  />
                                </div>
                              </div>

                              {/* Status and Remaining */}
                              <div className="flex justify-between items-center">
                                <div className="flex items-center space-x-2">
                                  {isAchieved ? (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                      Target Achieved!
                                    </span>
                                  ) : (
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${bgColor} ${textColor}`}>
                                      £{formatNumber(remaining)} remaining
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500">
                                  Rank: {customer.Rank}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>

                    {/* Summary Stats */}
                    {voucherData && (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">
                            {voucherData.filtered?.length || voucherData.length || 0}
                          </div>
                          <div className="text-sm text-gray-600">Total Customers</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {(voucherData.filtered || voucherData || []).filter(c => c.Sales >= c.Target).length}
                          </div>
                          <div className="text-sm text-gray-600">Targets Achieved</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-yellow-600">
                            {((voucherData.filtered || voucherData || []).filter(c => c.Sales >= c.Target).length / (voucherData.filtered?.length || voucherData.length || 1) * 100).toFixed(1)}%
                          </div>
                          <div className="text-sm text-gray-600">Success Rate</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">
                            £{formatNumber((voucherData.filtered || voucherData || []).reduce((sum, c) => sum + c.Sales, 0))}
                          </div>
                          <div className="text-sm text-gray-600">Total Sales</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 15 }} >
              <QuoteList selectedOperator={selectedOperator} />

            </div>
            <div style={{ marginTop: 15 }} >
              <CustomerSpendList selectedOperator={selectedOperator} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', marginTop: 15 }}>
              <MessageList selectedOperator={selectedOperator} />
              <VisitNotesList selectedOperator={selectedOperator} />

            </div>


            {activeTab === 'analytics' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-lg p-4">
                  <h3 className="font-bold mb-4">Sales Trends</h3>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={customerSpendsFiltered
                          .slice(0, 5)
                        }
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="GarageName" />
                        <YAxis />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="rebateAnalysis.netMonthSales"
                          name="Net Sales"
                          stroke="#8884d8"
                        />
                        <Line
                          type="monotone"
                          dataKey="rebateAnalysis.dailyMetrics.projectedMonthEnd"
                          name="Projected"
                          stroke="#82ca9d"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <h3 className="font-bold mb-4">Top Opportunities</h3>
                  <div className="space-y-4">
                    {customerSpendsFiltered
                      .filter(customer => {
                        const { nextTier, netMonthSales } = customer.rebateAnalysis || {};
                        if (!nextTier) return false;
                        const percentToNext = (netMonthSales / nextTier.target) * 100;
                        return percentToNext >= 80 && percentToNext < 100;
                      })
                      .map((customer, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
                        >
                          <AlertCircle className="text-yellow-500" />
                          <div>
                            <div className="font-semibold">{customer.GarageName}</div>
                            <div className="text-sm">
                              £{formatNumber(customer.rebateAnalysis?.nextTier?.amountNeeded || 0)} needed for next tier
                              ({customer.rebateAnalysis?.nextTier?.percent}%)
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedCustomer && (
        <MessageDialog
          isOpen={messageDialogOpen}
          onClose={() => setMessageDialogOpen(false)}
          accountId={selectedCustomer.id}
          garageName={selectedCustomer.garageName}
          operatorData={uinfo}
        />
      )}

      {/* Zero Spend Analysis Modal */}
      {showZeroSpendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <div className="flex items-center space-x-4">
                <h2 className="text-2xl font-bold text-gray-900">Zero Spend Analysis</h2>
                <div className="flex items-center space-x-3">
                  <BranchDropdown branch={modalSelectedBranch} setBranch={setModalSelectedBranch} />
                  <DateInput
                    value={modalSelectedDate}
                    onChange={setModalSelectedDate}
                    placeholder="Select date"
                    className="w-40"
                  />
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search operators..."
                      value={modalOperatorFilter}
                      onChange={(e) => setModalOperatorFilter(e.target.value)}
                      className="w-48 pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <svg className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowZeroSpendModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Enhanced Metrics Dashboard */}
                <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-6 rounded-xl border border-slate-200 shadow-lg">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {/* Total Sales Card */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-green-100 hover:shadow-md transition-all duration-300 group">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-600 mb-1">Total Sales</div>
                        <div className="text-3xl font-bold text-green-600 mb-2">
                          {branchSalesFetch.isLoading ? (
                            <div className="w-24 h-8 bg-gray-200 animate-pulse rounded"></div>
                          ) : branchSalesData?.data ? (
                            `£${formatNumber(branchSalesData.data.netSales)}`
                          ) : (
                            'No data'
                          )}
                        </div>
                        <div className="text-xs text-gray-500">Net revenue for selected period</div>
                      </div>
                    </div>

                    {/* Estimated Lost Sales Card */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-red-100 hover:shadow-md transition-all duration-300 group">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-600 mb-1">Estimated Lost Sales</div>
                        <div className="text-3xl font-bold text-red-600 mb-2">
                          {zeroSpendFetch.isLoading ? (
                            <div className="w-24 h-8 bg-gray-200 animate-pulse rounded"></div>
                          ) : zeroSpendAnalysis?.data?.summary ? (
                            `£${formatNumber(zeroSpendAnalysis.data.summary.estimatedLostSales)}`
                          ) : (
                            'No data'
                          )}
                        </div>
                        <div className="text-xs text-gray-500">Potential revenue from zero spenders</div>
                      </div>
                    </div>

                    {/* Zero Spenders Card */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-orange-100 hover:shadow-md transition-all duration-300 group">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-600 mb-1">Zero Spenders</div>
                        <div className="text-3xl font-bold text-orange-600 mb-2">
                          {matrixFetch.isLoading ? (
                            <div className="w-16 h-8 bg-gray-200 animate-pulse rounded"></div>
                          ) : operatorMatrix?.data ? (
                            (() => {
                              const totalCustomers = Object.values(operatorMatrix.data.rankTotals).reduce((sum, rank) => sum + rank.total, 0);
                              const totalSpent = Object.values(operatorMatrix.data.rankTotals).reduce((sum, rank) => sum + rank.spent, 0);
                              return totalCustomers - totalSpent;
                            })()
                          ) : (
                            'No data'
                          )}
                        </div>
                        <div className="text-xs text-gray-500">Customers with no spend activity</div>
                      </div>
                    </div>

                    {/* Date Card */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-blue-100 hover:shadow-md transition-all duration-300 group">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-600 mb-1">Analysis Date</div>
                        <div className="text-3xl font-bold text-blue-600 mb-2">
                          {modalSelectedDate ? moment(modalSelectedDate).format('DD/MM/YYYY') : 'No date selected'}
                        </div>
                        <div className="text-xs text-gray-500">Data snapshot period</div>
                      </div>
                    </div>
                  </div>
                </div>



                {/* Operator-Rank Matrix */}
                <div className="bg-white border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    {matrixFetch.isLoading ? (
                      <div className="flex items-center justify-center p-8">
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-gray-600">Loading operator matrix...</span>
                        </div>
                      </div>
                    ) : operatorMatrix?.data ? (
                      <table className="min-w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">Operator</th>
                            {['A', 'B', 'C', 'D', 'E', 'F'].map(rank => (
                              <th key={rank} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                Rank {rank}
                              </th>
                            ))}
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {operatorMatrix.data.operators
                            .filter(operator => {
                              if (!modalOperatorFilter) return true;
                              const searchTerm = modalOperatorFilter.toLowerCase();
                              const staffMember = matrixOperators.find(op => op.KeyCode === operator.inits);
                              const operatorName = staffMember?.a1 || '';
                              return operator.inits.toLowerCase().includes(searchTerm) ||
                                operatorName.toLowerCase().includes(searchTerm);
                            })
                            .map((operator, index) => {
                              // Find staff name from matrixOperators
                              const staffMember = matrixOperators.find(op => op.KeyCode === operator.inits);

                              // Clean debugging for staff name matching
                              if (operator.inits) {
                                if (!staffMember) {
                                  console.log(`❌ No match found for operator: "${operator.inits}"`);
                                  console.log(`   Available KeyCodes: [${salesOperators.map(op => op.KeyCode).join(', ')}]`);
                                } else {
                                  console.log(`✅ Found match: "${operator.inits}" → "${staffMember.a1}"`);
                                }
                              }

                              const displayName = staffMember ? `${staffMember.a1} (${operator.inits})` : operator.inits;

                              return (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r">
                                    <div className="font-semibold">{operator.inits}</div>
                                    {staffMember && (
                                      <div className="text-xs text-gray-500">{staffMember.a1}</div>
                                    )}
                                  </td>
                                  {['A', 'B', 'C', 'D', 'E', 'F'].map(rank => {
                                    const rankData = operator.ranks[rank];
                                    return (
                                      <td key={rank} className="px-4 py-3 text-center text-sm border-r">
                                        {rankData.total > 0 ? (
                                          <div className={`p-2 rounded ${getPercentageColor(rankData.percentage)}`}>
                                            <div className="font-semibold">
                                              {rankData.spent}/{rankData.total}
                                            </div>
                                            <div className="text-xs font-medium">
                                              ({rankData.percentage}%)
                                            </div>
                                          </div>
                                        ) : (
                                          <span className="text-gray-400">-</span>
                                        )}
                                      </td>
                                    );
                                  })}
                                  <td className="px-4 py-3 text-center text-sm font-semibold bg-gray-50">
                                    <div className={`p-2 rounded ${getPercentageColor(operator.totals.percentage)}`}>
                                      <div className="font-bold">
                                        {operator.totals.spent}/{operator.totals.total}
                                      </div>
                                      <div className="text-xs font-medium">
                                        ({operator.totals.percentage}%)
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          {/* Rank Totals Row */}
                          <tr className="bg-gray-100 border-t-2">
                            <td className="px-4 py-3 text-sm font-bold text-gray-900 border-r">
                              Total
                            </td>
                            {['A', 'B', 'C', 'D', 'E', 'F'].map(rank => {
                              const rankData = operatorMatrix.data.rankTotals[rank];
                              return (
                                <td key={rank} className="px-4 py-3 text-center text-sm font-bold border-r">
                                  <div className={`p-2 rounded ${getPercentageColor(rankData.percentage)}`}>
                                    <div>
                                      {rankData.spent}/{rankData.total}
                                    </div>
                                    <div className="text-xs font-medium">
                                      ({rankData.percentage}%)
                                    </div>
                                  </div>
                                </td>
                              );
                            })}
                            <td className="px-4 py-3 text-center text-sm font-bold bg-gray-200">
                              <div className={`p-2 rounded ${getPercentageColor(Math.round((Object.values(operatorMatrix.data.rankTotals).reduce((sum, rank) => sum + rank.spent, 0) /
                                Object.values(operatorMatrix.data.rankTotals).reduce((sum, rank) => sum + rank.total, 0)) * 100))}`}>
                                <div>
                                  {Object.values(operatorMatrix.data.rankTotals).reduce((sum, rank) => sum + rank.spent, 0)}/
                                  {Object.values(operatorMatrix.data.rankTotals).reduce((sum, rank) => sum + rank.total, 0)}
                                </div>
                                <div className="text-xs font-medium">
                                  ({Math.round((Object.values(operatorMatrix.data.rankTotals).reduce((sum, rank) => sum + rank.spent, 0) /
                                    Object.values(operatorMatrix.data.rankTotals).reduce((sum, rank) => sum + rank.total, 0)) * 100)}%)
                                </div>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        No operator matrix data available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
        )}
      </div>
    </div>
  );
}

const formatNumber = (number) => {
  if (isNaN(number) || number === null || number === undefined) return '0.00';
  return new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number);
};

function CurrentUserHeader({
  uinfo,
  currentUserData,
  isLoading,
  isToday,
  isGross,
  selectedOperator,
  setSelectedOperator,
  salesOperators
}) {
  const [showPopup, setShowPopup] = useState(false);

  const dataArray = Array.isArray(currentUserData) ? currentUserData : [currentUserData];
  //console.log('Full currentUserData content:', JSON.stringify(currentUserData, null, 2));

  // Use Branch instead of OpBranch
  const mainBranchData = dataArray.find(d =>
    d.Branch === selectedOperator.Branch
  ) || {};

  const otherBranches = dataArray.filter(d =>
    d.Branch !== selectedOperator.Branch
  );

  const periodKey = isToday ? 'Today' : 'Month';

  // Calculate main figures
  const invoiceValue = parseFloat(mainBranchData[`invoiceValue${periodKey}`]) || 0;
  const creditsValue = parseFloat(mainBranchData[`creditsValue${periodKey}`]) || 0;
  const sales = isGross ? invoiceValue : invoiceValue + creditsValue;
  const margin = parseFloat(mainBranchData[`margin${periodKey}`]) || 0;
  const marginPercent = parseFloat(mainBranchData[`marginPercent${periodKey}`]) || 0;
  const credits = isGross ? creditsValue : null;

  const target = selectedOperator.SalesTarget || 2000;
  const progressPercent = (sales / target) * 100;
  const remainingAmount = target - sales;

  const getProgressColor = (percent) => {
    if (percent >= 100) return 'bg-green-500';
    if (percent >= 75) return 'bg-blue-500';
    if (percent >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const formatNumber = (number) => {
    if (isNaN(number) || number === null || number === undefined) return '0.00';
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  return (
    <header className="p-6 mb-8 w-1/2 h-full text-center bg-white rounded-lg shadow-lg">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">
          {selectedOperator.Name} - {selectedOperator.Inits}

        </h2>
        <div style={{ display: 'flex', flexDirection: 'column' }}>{uinfo.Status === 'admin' && (
          <select
            value={selectedOperator.Username}
            onChange={(e) => {
              const chosen = salesOperators.find(op => op.Username === e.target.value);
              setSelectedOperator(chosen || uinfo);
            }}
            className="p-2 rounded border"
          >
            <option value={uinfo.Username}>My Data</option>
            {salesOperators.map((operator) => (
              <option key={operator.Username} value={operator.Username}>
                {operator.Name} [{operator.Branch}]
              </option>
            ))}
          </select>
        )}
          {otherBranches.length > 0 && (
            <span
              className="relative inline-block ml-2 text-sm text-blue-500 border-b border-dotted cursor-pointer"
              onMouseEnter={() => setShowPopup(true)}
              onMouseLeave={() => setShowPopup(false)}
            >
              Other Branches
              {showPopup && (
                <div className="absolute z-10 p-4 bg-white border rounded shadow-lg min-w-[200px] top-6 left-0">
                  {otherBranches.map((branch, i) => (
                    <div key={i} className="mb-4 last:mb-0 text-left">
                      <p className="font-bold">{branch.Branch}</p>
                      <p>Sales: £{formatNumber(parseFloat(branch[`invoiceValue${periodKey}`]))}</p>
                      <p>Credits: £{formatNumber(parseFloat(branch[`creditsValue${periodKey}`]))}</p>
                      <p>Margin: £{formatNumber(parseFloat(branch[`margin${periodKey}`]))}</p>
                      <p>Margin %: {formatNumber(parseFloat(branch[`marginPercent${periodKey}`]))}%</p>
                    </div>
                  ))}
                </div>
              )}
            </span>
          )}
        </div>
      </div>

      <div className="flex justify-around mt-4">
        <div>
          <p className="text-lg">Sales</p>
          <div className="text-2xl font-semibold text-green-600">
            {isLoading ? (
              <SkeletonGroup>
                <Skeleton>£0.00</Skeleton>
              </SkeletonGroup>
            ) : (
              `£${formatNumber(sales)}`
            )}
          </div>
        </div>
        {uinfo.Status === 'admin' && (
          <div>
            <p className="text-lg">Margin</p>
            <p className="text-2xl font-semibold text-blue-600">
              £{formatNumber(margin)}
            </p>
          </div>
        )}
        <div>
          <p className="text-lg">Margin %</p>
          <p className="text-2xl font-semibold text-purple-600">
            {formatNumber(marginPercent)}%
          </p>
        </div>
        {isGross && (
          <div>
            <p className="text-lg">Credits</p>
            <p className="text-2xl font-semibold text-red-600">
              £{formatNumber(credits)}
            </p>
          </div>
        )}
      </div>

      <div className="mt-6">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600">Progress to Target</span>
          <span className="text-gray-600">{formatNumber(progressPercent)}%</span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getProgressColor(progressPercent)}`}
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-sm mt-1">
          <span className="text-gray-600">
            Target: £{formatNumber(target)}
          </span>
          <span className="text-gray-600">
            Remaining: £{formatNumber(Math.max(remainingAmount, 0))}
          </span>
        </div>
      </div>
    </header>
  );
}


function OtherStaffTable({ branch, otherStaff, isToday, isGross }) {
  const { data: salesMarginByBranchAndInitsToday } = useApi({ url: '/salesassist/cplusassist/salesMarginByBranchAndInitsToday', initial: [] })

  const sortedData = [...otherStaff].sort((a, b) => {
    const key = isToday ? 'Today' : 'Month';
    const column = isGross ? `invoiceValue${key}` : `invoiceValue${key}` - `creditsValue${key}`;
    return b[column] - a[column];
  });

  const branchTarget = branch === 'All' ? (
    Object.values(
      salesMarginByBranchAndInitsToday.reduce((acc, cur) => {
        acc[cur.branch] = cur.salesTarget
        return acc
      }, {})
    ).reduce((acc, cur) => {
      acc = acc + cur
      return acc
    }, 0)
  ) : (
    salesMarginByBranchAndInitsToday.reduce((acc, cur) => {
      if (cur.branch === branch.MamCode) acc = cur.salesTarget
      return acc
    }, 0)
  )

  const totalSold = sortedData.reduce((acc, staff) => {
    const periodKey = isToday ? 'Today' : 'Month';
    const sales = isGross ? staff[`invoiceValue${periodKey}`] : staff[`invoiceValue${periodKey}`] + staff[`creditsValue${periodKey}`]
    return acc += sales
  }, 0)

  const totalSoldPercentOfTarget = (totalSold / branchTarget) * 100
  const targetDistanceAlongLine = 10000 / totalSoldPercentOfTarget

  const operatorBarChunks = sortedData
    .reduce((acc, cur) => {
      const periodKey = isToday ? 'Today' : 'Month';
      const sales = isGross ? cur[`invoiceValue${periodKey}`] : cur[`invoiceValue${periodKey}`] + cur[`creditsValue${periodKey}`]
      const totalSalesOrTarget = totalSold > branchTarget ? totalSold : branchTarget
      const opPercentOfTarget = (sales / totalSalesOrTarget) * 100

      if (opPercentOfTarget < 4) {
        const othersInd = acc.findIndex((el) => el.Name === 'Others')

        if (othersInd === -1) {
          acc.push({ Name: 'Others', Sales: sales, Width: opPercentOfTarget, Id: 'Others' })
        } else {
          acc[othersInd].Sales += sales
          const newPercentOfTarget = (acc[othersInd].Sales / totalSalesOrTarget) * 100
          acc[othersInd].Width = newPercentOfTarget
        }
      } else {
        acc.push({ Name: cur.OperatorName || 'Unknown', Sales: sales, Width: opPercentOfTarget, Id: cur._id })
      }

      return acc
    }, [])
    .sort((a, b) => a.Sales - b.Sales)

  const colors = [
    'rgb(6, 182, 212)',
    'rgb(16, 185, 129)',
    'rgb(245, 158, 11)',
    'rgb(251, 113, 133)'
  ]

  return (
    <>
      <div className="p-4 mt-6 bg-white rounded shadow-md">
        <div className="flex justify-between mb-4 text-lg font-bold">
          <div>Operator performance ({branch === 'All' ? branch : branch.Branch} - {isToday ? 'Day' : 'Month'})</div>
          <div>£{formatNumber(totalSold)} sold of £{formatNumber(branchTarget)} target ({totalSoldPercentOfTarget.toFixed(0)}%)</div>
        </div>
        <div className="relative mt-8 w-full h-6">
          {
            branchTarget && (
              <>
                <div
                  className="absolute p-1 text-white bg-red-500 rounded shadow-lg"
                  style={{
                    left: `${targetDistanceAlongLine}%`,
                    top: '-20px',
                    transform: 'translateX(-50%)',
                    color: 'white',
                    fontWeight: 600,
                    zIndex: 1001,
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                  }}
                >
                  £{formatNumber(branchTarget)} Target
                </div>

                <div
                  className="w-[3px] h-[50px] bg-red-500 border-1 border-white absolute shadow-lg"
                  style={{
                    zIndex: 1000,
                    transform: 'translateX(-50%)',
                    top: 0,
                    left: `${targetDistanceAlongLine}%`,
                  }}
                />
              </>
            )
          }
        </div>
        <div className="overflow-hidden relative w-full h-14 bg-white rounded-full">
          {
            operatorBarChunks.map((chunk, ind) => (
              <div
                className="inline-flex overflow-hidden relative justify-center items-center h-full whitespace-nowrap"
                style={{
                  width: `${chunk.Width}%`,
                  backgroundColor: colors[ind % colors.length],
                  color: 'white',
                  fontWeight: 600,
                  textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                }}
              >
                {chunk.Name}
              </div>
            ))
          }
          <div
            className="w-[3px] h-full bg-red-500 outline outline-1 outline-white absolute shadow-lg"
            style={{
              zIndex: 1000,
              transform: 'translateX(-50%)',
              top: 0,
              left: `${targetDistanceAlongLine}%`,
            }}
          />
        </div>
      </div>

      <table className="mt-4 min-w-full bg-white border border-gray-200 border-collapse">
        <thead>
          <tr>
            <th className="p-2 border-b">Name</th>
            <th className="p-2 border-b">Sales</th>
            <th className="p-2 border-b">Margin</th>
            <th className="p-2 border-b">Margin %</th>
            {isGross && <th className="p-2 border-b">Credits</th>}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((staff, index) => {
            const periodKey = isToday ? 'Today' : 'Month';
            const sales = isGross ? staff[`invoiceValue${periodKey}`] : staff[`invoiceValue${periodKey}`] + staff[`creditsValue${periodKey}`];
            const margin = staff[`margin${periodKey}`];
            const marginPercent = staff[`marginPercent${periodKey}`];
            const credits = isGross ? staff[`creditsValue${periodKey}`] : null;

            return (
              <tr
                key={index}
                className="text-center border-b hover:bg-gray-100"
              >
                <td className="p-2">{staff.OperatorName || 'Unknown'}</td>
                <td className="p-2">£{formatNumber(sales)}</td>
                <td className="p-2">£{formatNumber(margin)}</td>
                <td className="p-2">{formatNumber(marginPercent)}%</td>
                {isGross && <td className="p-2">£{formatNumber(credits)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

export default Dashboard;